import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "../shared/icons";

/*
 * Full-screen vertical video feed, opened from the Assets tab.
 *
 * Scroll (or swipe, or arrow keys) to move between videos, one per screen, the way a social
 * feed works. CSS scroll snapping does the movement — no scroll maths, so it keeps the
 * platform's own inertia and rubber-banding instead of fighting them.
 *
 * The thing that actually matters here is NOT loading everything. These clips run 100–300 MB
 * and there are eighteen of them, so a naive feed would ask for ~2 GB the moment it opened.
 * Every <video> carries preload="none" and only the visible one is told to play, so the
 * browser fetches exactly one clip at a time and streams it by range request.
 *
 * Muted autoplay is deliberate: unmuted autoplay is blocked by every browser, and eighteen
 * clips blaring as you scroll would be worse than useless in front of a customer. The native
 * controls are left on so a rep can unmute and scrub the one they stopped at.
 */
const VideoFeed = ({ items, startIndex = 0, onClose, has, onAdd }) => {
  const { t } = useTranslation();
  const scrollerRef = useRef(null);
  const slideRefs = useRef([]);
  const [active, setActive] = useState(startIndex);

  /* Jump to the clip the user picked before the first paint, so the feed never flashes the
     first video on its way to the right one. */
  useEffect(() => {
    const el = slideRefs.current[startIndex];
    if (el) el.scrollIntoView({ block: "start", behavior: "auto" });
  }, [startIndex]);

  /* Whichever slide is mostly on screen becomes the active one. An observer rather than a
     scroll handler, so this costs nothing while the user is flicking. */
  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            const i = slideRefs.current.indexOf(e.target);
            if (i >= 0) setActive(i);
          }
        });
      },
      { root, threshold: [0.6] },
    );
    slideRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  /* Exactly one clip plays. Everything else is paused and rewound, which also releases the
     decoder on mobile where only a couple of videos can be live at once. */
  useEffect(() => {
    slideRefs.current.forEach((slide, i) => {
      const v = slide && slide.querySelector("video");
      if (!v) return;
      if (i === active) {
        const p = v.play();
        /* Swallowed on purpose. Two refusals are normal and neither is recoverable here:
           a browser that will not autoplay without a gesture, and Chrome pausing muted
           video-only media when the surface is treated as background ("paused to save
           power"). The native controls are right there either way. */
        if (p && p.catch) p.catch(() => {});
      } else {
        v.pause();
        if (v.currentTime) v.currentTime = 0;
      }
    });
  }, [active, items.length]);

  const step = useCallback((delta) => {
    const next = Math.min(items.length - 1, Math.max(0, active + delta));
    const el = slideRefs.current[next];
    if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [active, items.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowDown" || e.key === "PageDown") { e.preventDefault(); step(1); }
      if (e.key === "ArrowUp" || e.key === "PageUp") { e.preventDefault(); step(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, step]);

  return (
    <div className="cn-feed" role="dialog" aria-modal="true" aria-label={t("co_assets_videos")}
      data-active={active}>
      <div className="cn-feedbar">
        <span className="cn-feedcount">
          {t("co_feed_of", { i: active + 1, n: items.length })}
        </span>
        <button className="cn-feedx" onClick={onClose} aria-label={t("co_feed_close")}>✕</button>
      </div>

      <div className="cn-feedscroll" ref={scrollerRef}>
        {items.map((it, i) => (
          <section className="cn-slide" key={it.fid}
            ref={(el) => { slideRefs.current[i] = el; }}>
            <video
              className="cn-feedvid"
              src={it.src}
              /* none, not metadata: eighteen clips at 100–300 MB each would be ~2 GB of
                 requests on open. The active slide's play() is what starts the fetch. */
              preload="none"
              controls
              muted
              playsInline
              loop
            />
            <div className="cn-feedmeta">
              <h3>{t(it.title)}</h3>
              <p>{it.source}</p>
              <p className="cn-feedsub">{t(it.meta)}</p>
              <div className="cn-feedact">
                <button className="cn-btn" style={{ marginTop: 0 }} onClick={() => onAdd(it.fid)}>
                  {has(it.fid) ? `${t("co_added")} ✓` : <><Icon.Plus />{t("co_add_file")}</>}
                </button>
                {i < items.length - 1 && (
                  <span className="cn-feedhint">{t("co_feed_hint")}</span>
                )}
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default VideoFeed;
